import {ActionsTransform} from "./ActionsTransform";
import {assistantsTransform} from "../assistants/AssistantsTransform";

import {
  APIMethodEnum,
  AuthorizationTypeEnum,
  CustomActionRecordModel,
  CustomActionListModel,
  CustomActionCreateModel,
  CustomActionUpdateModel,
  ActionTypeModel,
} from "@/api/models";
import {
  CustomAction,
  PaginatedResponse,
  KeyValuePair,
  LLMVariableType,
  Authentication,
} from "@/api/services";
import {BaseAction} from "@/types/api";
import {AuthenticationValue} from "@/types/node";

export class ActionCustomTransform extends ActionsTransform<
  CustomActionRecordModel,
  CustomActionCreateModel,
  CustomActionUpdateModel
> {
  private mapTypeToAuthEnum(type?: string): AuthorizationTypeEnum {
    if (!type) return AuthorizationTypeEnum.Custom;
    const typeToAuthEnumMap: Record<string, AuthorizationTypeEnum> = {
      ghl: AuthorizationTypeEnum.GoHighLevel,
      hubspot: AuthorizationTypeEnum.Hubspot,
      salesforce: AuthorizationTypeEnum.Salesforce,
    };
    return typeToAuthEnumMap[type] || AuthorizationTypeEnum.Custom;
  }

  /**
   * INFO: temporary workaround to satisfy the current backend interface.
   * Wraps unquoted variables in quotes to make JSON valid for parsing.
   * Only wraps variables that are not already quoted.
   */
  private wrapVariablesInQuotes(body: string): string {
    // Pattern to match variables that are not already quoted
    // This matches <variable> but not "<variable>" or '<variable>'
    const unquotedVariablePattern = /(?<!["'])<([^>]+)>(?!["'])/g;

    return body.replace(
      unquotedVariablePattern,
      function (match, variableName) {
        return `"<${variableName}>"`;
      },
    );
  }

  /**
   * INFO: temporary workaround to satisfy the current frontend interface.
   * Removes quotes from variables based on their type in llm_vars.
   * Only removes quotes for non-string types (int, float, bool, list).
   */
  private removeQuotesFromVariables(
    body: string,
    llmVars: Array<{name?: string; type?: string}>,
  ): string {
    if (!llmVars || llmVars.length === 0) {
      return body;
    }

    // Create a map of variable names to their types
    const variableTypeMap = new Map<string, string>();
    llmVars.forEach(variable => {
      if (variable.name && variable.type) {
        variableTypeMap.set(variable.name, variable.type);
      }
    });

    // Pattern to match quoted variables: "<variable>"
    const quotedVariablePattern = /"<([^>]+)>"/g;

    return body.replace(quotedVariablePattern, function (match, variableName) {
      const variableType = variableTypeMap.get(variableName);

      // Remove quotes for non-string types
      if (variableType && variableType !== "string") {
        return `<${variableName}>`;
      }

      // Keep quotes for string type or unknown type
      return match;
    });
  }

  private makeAuthenticationValue(
    actionDto: CustomAction,
  ): AuthenticationValue {
    const authentication = actionDto.authentication;
    let auth = {};

    if (authentication) {
      auth = {
        [actionDto.child_type]: {
          selected: true,
          [`${actionDto.child_type}_auth`]: {
            authType: "authentication",
            data: {
              location: "header",
              key: authentication.key,
              value: authentication.value,
              type: "authentication",
            },
          },
        },
      };
    }
    return auth;
  }

  public makeRecordModel(
    dto: CustomAction & BaseAction,
  ): CustomActionRecordModel {
    const model: CustomActionRecordModel = {
      ...dto,
      title: dto.name,
      description: dto.description || "",
      type: dto.child_type
        ? this.mapTypeToAuthEnum(dto.child_type)
        : AuthorizationTypeEnum.Custom,
      apiUrl: dto.url || "",
      apiMethod: dto.http_method as APIMethodEnum,

      apiHeaders: Object.entries(dto?.headers || {}).map(([key, value]) => ({
        key,
        value,
      })) as KeyValuePair[],

      parameters: dto.config_params,
      isAuthorizationNeeded: !!dto.authentication,
      authKey: dto.authentication?.key || "",
      authValue: dto.authentication?.value || "",
      assistants: (dto.assistants || []).map(assistant =>
        assistantsTransform.makeEvenCompactModel(assistant),
      ),
      speech: dto.speech || "",
      llm_vars: (dto.llm_vars || [])
        .filter(variable => variable !== null && variable !== undefined)
        .map(variable => ({
          key: variable.name,
          description: variable.description,
          type: variable.type,
          example: variable.example,
        })),
      variablesBeforeTheCall: Object.entries(
        dto.variables_before_the_call || {},
      ).map(([key, value]) => ({
        key,
        value: typeof value === "string" ? value : "",
      })),
      queryParameters: Object.entries(dto.query_params || {}).map(
        ([key, value]) => ({
          key,
          value,
        }),
      ) as KeyValuePair[],

      body: dto.body
        ? this.removeQuotesFromVariables(
            JSON.stringify(dto.body, null, 2),
            dto.llm_vars || [],
          )
        : "{}",
      isSpeakingNaturally: dto.speak_naturally || false,
      startMessage: dto.start_message || "",
      delayMessage: dto.delay_message || "",
      failureMessage: dto.failure_message || "",
      delayMessageTime: dto.delay_threshold?.toString() || "",
      failure_timeout: dto.failure_timeout,
      run_action_before_call_start: dto.run_action_before_call_start || false,
      api_response: dto.api_response || {},
      variables: dto.variables || [],
      query_params: dto.query_params,

      nodeData: {
        action_id: dto.action_id,
        slug: dto.slug,
        type: ActionTypeModel.CUSTOM,
        endpoint: {
          url: dto.url || "",
          http_method: dto.http_method as APIMethodEnum,
        },
        headers: Object.entries(dto?.headers || {}).map(([key, value]) => [
          {
            key: "key",
            value: key,
          },
          {
            key: "value",
            value: value,
          },
        ]),
        authentication: this.makeAuthenticationValue(dto) || {},
        body_and_variables: {
          json: {
            selected: true,
            body: dto.body
              ? this.removeQuotesFromVariables(
                  JSON.stringify(dto.body, null, 2),
                  dto.llm_vars || [],
                )
              : "{}",
          },
          variables: {
            selected: true,
            variables: (dto.llm_vars || []).map(variable => [
              {
                key: "key",
                value: variable.name,
              },
              {
                key: "type",
                value: variable.type,
              },
              {
                key: "value",
                value: variable.example,
              },
              {
                key: "description",
                value: variable.description,
              },
            ]),
          },
          query_parameters: {
            selected: dto.http_method === APIMethodEnum.GET,
            query_parameters: Object.entries(dto.query_params || {}).map(
              ([key, value]) => [
                {
                  key: "key",
                  value: key,
                },
                {
                  key: "value",
                  value: value,
                },
              ],
            ),
          },
        },
        speak_naturally: dto.speak_naturally,
        start_message: dto.start_message,
        delay_message: dto.delay_message,
        failure_message: dto.failure_message,
        delay_message_time: dto.delay_threshold?.toString() || "",
        failure_timeout: dto.failure_timeout,
        run_action_before_call_start: dto.run_action_before_call_start,
        name: dto.name,
        description: dto.description,
        variables: dto.variables,
      },
    };

    return model;
  }

  public makeListModel(dto: PaginatedResponse): CustomActionListModel {
    const model: CustomActionListModel = {
      records: (dto.items || []).map(item =>
        this.makeRecordModel(item as CustomAction & BaseAction),
      ),
      pagination: this.makePaginationModel(dto),
    };

    return model;
  }

  public makeCreateDto(model: CustomActionCreateModel): CustomAction {
    const dto: Partial<CustomAction> = {
      type: "custom",
      workspace_id: model.workspaceId,
      name: model.title,
      http_method: model.apiMethod,
      child_type: model.type,
      url: model.apiUrl,
      description: model.description,
      assistants: [],
      run_action_before_call_start: false,
      config_params: {},
      query_params: {},
      llm_vars: [],
      variables: [],
      variables_before_the_call: {},
      body: {},
      api_response: {},
      speak_naturally: false,
      delay_threshold: 7,
      failure_timeout: 0,
    };

    return dto as CustomAction;
  }

  public makeUpdateDto(model: CustomActionUpdateModel): CustomAction {
    const dto: Partial<CustomAction> = {};

    // Basic Information
    if ("title" in model) dto.name = model.title;
    if ("description" in model) dto.description = model.description;
    if ("speech" in model) dto.speech = model.speech;
    if ("apiMethod" in model) dto.http_method = model.apiMethod;
    if ("apiUrl" in model) dto.url = model.apiUrl;
    if ("apiHeaders" in model) {
      if (model.apiHeaders.length === 0) {
        dto.headers = null;
      } else {
        const headers = {};
        model.apiHeaders.forEach(header => {
          headers[header.key] = header.value;
        });
        dto.headers = headers;
      }
    }
    if ("isAuthorizationNeeded" in model) {
      dto.authentication = model.isAuthorizationNeeded ? {} : null;
    }
    // Authentication
    if ("authKey" in model) {
      dto.authentication = {
        ...dto.authentication,
        key: model.authKey,
      };
    }
    if ("authValue" in model) {
      dto.authentication = {
        ...dto.authentication,
        value: model.authValue,
      };
    }
    if ("type" in model) {
      dto.child_type = model.type;
    }

    if ("variablesBeforeTheCall" in model) {
      if (model.variablesBeforeTheCall.length === 0) {
        dto.variables_before_the_call = null;
      } else {
        const variables = {};
        model.variablesBeforeTheCall.forEach(variable => {
          variables[variable.key] = variable.value;
        });
        dto.variables_before_the_call = variables;
      }
    }
    if ("variables" in model) {
      dto.variables = model.variables;
    }

    if ("llm_vars" in model && Array.isArray(model.llm_vars)) {
      dto.llm_vars = model.llm_vars
        .filter(variable => variable !== null && variable !== undefined)
        .map(variable => ({
          name: variable.key,
          description: variable.description,
          type: variable.type as LLMVariableType,
          example: variable.example,
        }));
    }

    if ("queryParameters" in model) {
      if (model.queryParameters.length === 0) {
        dto.query_params = null;
      } else {
        const params = {};
        model.queryParameters.forEach(param => {
          params[param.key] = param.value;
        });
        dto.query_params = params;
      }
    }
    if ("body" in model) {
      dto.body = JSON.parse(this.wrapVariablesInQuotes(model.body));
    }
    if ("isSpeakingNaturally" in model) {
      dto.speak_naturally = model.isSpeakingNaturally;
    }
    if ("startMessage" in model) {
      dto.start_message = model.startMessage;
    }
    if ("delayMessage" in model) {
      dto.delay_message = model.delayMessage;
    }
    if ("failureMessage" in model) {
      dto.failure_message = model.failureMessage;
    }
    if ("delayMessageTime" in model) {
      dto.delay_threshold = parseInt(model.delayMessageTime);
    }
    if ("failure_timeout" in model) {
      dto.failure_timeout = model.failure_timeout;
    }

    if ("run_action_before_call_start" in model) {
      dto.run_action_before_call_start = model.run_action_before_call_start;
    }

    if ("api_response" in model) {
      dto.api_response = model.api_response;
    }

    return dto as CustomAction;
  }
}

export const actionCustomTransform = new ActionCustomTransform();
